import { Fragment, FunctionComponent, PropsWithChildren } from 'react';
import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { OrderStatus, Ticket } from '@/models/ticket';
import { formatStarjackDate } from '@/utils/time';
import { getCountryString } from '@/constants/countryOptions';

type TicketsDetailsCardProps = {
    ticket: Ticket;
};

export const TicketsDetailsCard: FunctionComponent<TicketsDetailsCardProps> = ({
    ticket,
}) => {
    if (!ticket) return null;

    return (
        <Card
            sx={{
                maxWidth: '640px',
            }}
        >
            <CardContent>
                <Typography variant='h6'>Ticket Daten</Typography>
            </CardContent>
            <Divider
                sx={{
                    width: '100%',
                }}
            />

            {ticket.skiers.map((skier, index, skiers) => {
                const showIndex = skiers.length > 1;

                return (
                    <Fragment key={index}>
                        <TicketDetailsCardRow>
                            <Typography variant='subtitle2'>
                                Gast{showIndex && ` (${index + 1})`}:
                            </Typography>
                            <Typography
                                sx={{
                                    ml: 2,
                                    mt: 2,
                                }}
                            >
                                {skier.firstname} {skier.lastname}
                            </Typography>
                        </TicketDetailsCardRow>
                    </Fragment>
                );
            })}

            {ticket.identificationType === 'KEYCARD' && (
                <>
                    {ticket.identifications.map((ident, index, idents) => {
                        return (
                            <TicketDetailsCardRow key={index}>
                                <Typography variant='subtitle2'>
                                    Keycardbestellung Status - Gast {index + 1}
                                    :{' '}
                                </Typography>
                                <Typography
                                    sx={{
                                        ml: 2,
                                        mt: 2,
                                    }}
                                >
                                    {getOrderStatusAsString(ident.status)}
                                </Typography>

                                {index !== idents.length - 1 && (
                                    <Divider sx={{ width: '100%' }} />
                                )}
                            </TicketDetailsCardRow>
                        );
                    })}
                </>
            )}

            <Divider
                sx={{
                    width: '100%',
                }}
            />

            <TicketDetailsCardRow>
                <Typography variant='subtitle2'>Bestelldatum:</Typography>
                <Typography
                    sx={{
                        ml: 2,
                        mt: 2,
                    }}
                >
                    {formatStarjackDate(ticket.orderDate)}
                </Typography>
            </TicketDetailsCardRow>

            <Divider
                sx={{
                    width: '100%',
                }}
            />

            <TicketDetailsCardRow>
                <Typography variant='subtitle2'>Startdatum:</Typography>
                <Typography
                    sx={{
                        ml: 2,
                        mt: 2,
                    }}
                >
                    {formatStarjackDate(ticket.startDate)}
                </Typography>
            </TicketDetailsCardRow>

            <Divider
                sx={{
                    width: '100%',
                }}
            />

            <TicketDetailsCardRow>
                <Typography variant='subtitle2'>Adresse:</Typography>
                <Typography
                    sx={{
                        ml: 2,
                        mt: 2,
                    }}
                >
                    {ticket.deliveryAddress.firstname}{' '}
                    {ticket.deliveryAddress.lastname}
                </Typography>

                <Typography
                    sx={{
                        ml: 2,
                        mt: 2,
                    }}
                >
                    {ticket.deliveryAddress.street}
                </Typography>

                <Typography
                    sx={{
                        ml: 2,
                        mt: 2,
                    }}
                >
                    {ticket.deliveryAddress.zipcode},{' '}
                    {ticket.deliveryAddress.city}
                </Typography>

                <Typography
                    sx={{
                        ml: 2,
                        mt: 2,
                    }}
                >
                    {getCountryString('de', ticket.deliveryAddress.country)}
                </Typography>
            </TicketDetailsCardRow>
        </Card>
    );
};

const TicketDetailsCardRow: FunctionComponent<PropsWithChildren<unknown>> = ({
    children,
}) => (
    <Box
        sx={{
            p: 4,
            pl: 6,
            width: '100%',
        }}
    >
        {children}
    </Box>
);

const getOrderStatusAsString = (status: OrderStatus) => {
    switch (status) {
        case 'ORDERED':
            return 'Bestellt';
        case 'PRODUCED':
            return 'Produziert';
        case 'PAID':
            return 'Bezahlt';
        default:
            return status;
    }
};
